import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import dayjs from 'dayjs';

import styles from './UserPromotionFormTable.module.scss';
import i18n from '~/i18n';
import { Link } from 'react-router-dom';

interface IPromoLoyalty {
  id: number;
  name: string;
}

interface IPromotionTableProps {
  data: {
    points: number;
    created_at: string;
    promo_loyalty: IPromoLoyalty | null;
    manual_action: boolean | null;
    food_order_id: number | null;
    by_user: {
      full_name: string;
      id: number;
    };
    order_info: {
      food_order_id: number | null;
      id: number | null;
      type: string | null;
    };
  }[];
  sort: { field: string; direction: string };
  setSort: ({ field, direction }: { field: string; direction: string }) => void;
}

interface IPromotionLabelProps {
  manualAction: boolean | null;
  points: number;
  user: {
    full_name: string;
    id: number;
  };
  orderInfo: {
    food_order_id: number | null;
    id: number | null;
    type: string | null;
  };
  foodOrderId: number | null;
  promo_loyalty: IPromoLoyalty | null;
}

const PromotionLabel = ({
  manualAction,
  points,
  user,
  orderInfo,
  foodOrderId,
  promo_loyalty,
}: IPromotionLabelProps) => {
  // Ручное начисление/списание
  if (manualAction === true) {
    return (
      <p className={styles.cashbackRow}>
        {points < 0 ? i18n.t('manual_debit') : i18n.t('manual_accrual')}
        <span>/</span>
        <Link to={`/user/${user.id}`} className={styles.link}>
          {user.full_name}
        </Link>
      </p>
    );
  }

  // Кэшбек по заказу (проверяем оба поля: order_info.food_order_id и food_order_id)
  const actualFoodOrderId = orderInfo?.food_order_id || foodOrderId;
  if (actualFoodOrderId) {
    return (
      <p className={styles.cashbackRow}>
        {i18n.t('cashback_percent')}
        <span>/</span>
        <Link
          to={`/transactions/${actualFoodOrderId}`}
          className={styles.link}
        >
          #{actualFoodOrderId}
        </Link>
      </p>
    );
  }

  // Начисление/списание по акции
  if (promo_loyalty) {
    return (
      <p className={styles.cashbackRow}>
        {points < 0 ? i18n.t('promotion_debit') : i18n.t('promotion_accural')}
        <span>/</span>
        <Link to={`/promotions/${promo_loyalty.id}`} className={styles.link}>
          {promo_loyalty.name}
        </Link>
      </p>
    );
  }

  // Удалённая запись
  return <span>{i18n.t('manual_delete')}</span>;
};

const PointQuantity = ({ point }: { point: number }) => {
  const isPositive = point > 0;

  return (
    <div className={isPositive ? styles.positive : styles.negative}>
      {isPositive
        ? `+${point} ${i18n.t('points')}`
        : `${point} ${i18n.t('points')}`}
    </div>
  );
};

export const UserPromotionFormTable = ({
  data,
  sort,
  setSort,
}: IPromotionTableProps) => {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHead sort={sort} setSort={setSort}>
        <TableCellHead field="created_at">{t('date')}</TableCellHead>
        <TableCellHead field="points">{t('write_off_accrual')}</TableCellHead>
        <TableCellHead field="" sortable={false}>{t('promotion')}</TableCellHead>
      </TableHead>
      <TableBody>
        {data.map((item, idx) => (
          <TableRow key={idx}>
            <TableCell>{dayjs(item.created_at).format('DD.MM.YYYY')}</TableCell>
            <TableCell>
              <PointQuantity point={item.points} />
            </TableCell>
            <TableCell>
              <PromotionLabel
                manualAction={item.manual_action}
                points={item.points}
                user={item.by_user}
                orderInfo={item.order_info}
                foodOrderId={item.food_order_id}
                promo_loyalty={item.promo_loyalty}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
